import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { getFileActionWithRetry } from '../../../../redux/files/actions';
import uploadFilesHelper from '../../../../js/uploadFiles';
import { Dropdown, Tag, Tooltip } from 'antd';
import _debounce from 'lodash/debounce';
import {
  Mail, Calendar, Type, ChevronDown, Flag, Hash, DollarSign, LayoutList,
  Star, Phone, AlignLeft, FileText, CheckSquare, Users, Calculator,
  UserRound, MapPin, Clock, AlarmClock, Banknote, Percent, Timer, Binary,
  Globe, Palette, Paperclip, Camera, PenLine, User, Link2, CircleDot,
} from 'lucide-react';
import { TimePicker, formatTimeDisplay } from '../../organisms/PublicFormFields/TimeField';

import { TaskButtons } from '../../atoms/buttons/TaskButtons';
import Icon from '../../atoms/global/Icon';
import { customFieldTypes, fieldTypes } from '../../../../js/constants';
import PriorityDropdown from '../task-components/PriorityDropdown';
import CalendarDropdown from '../task-components/CalendarDropdown';
import { getTagColor, isValidURL } from '../../../../js/helper';
import { FormattedMessage, useIntl } from 'react-intl';
import { editTaskCustomFieldAction } from '../../../../redux/tasks/actions';
import MultiSelectCustomField from '../../atoms/task-panel-customfields/MultiSelectCustomField';
import { toggleEditCustomFieldModal } from '../../../../actions/modalAction';
import EmailCustomField from '../../atoms/task-panel-customfields/EmailCustomField';
import RatingCustomField from '../../atoms/task-panel-customfields/RatingCustomField';
import ConnectedAssigneeSelect from '../task-components/ConnectedAssigneeSelect';
import PhoneCustomField from '../../atoms/task-panel-customfields/PhoneCustomField';
import LongTextCustomField from '../../atoms/task-panel-customfields/LongTextCustomField';
import RichTextCustomField from '../../atoms/task-panel-customfields/RichTextCustomField';
import ColorCustomField from '../../atoms/task-panel-customfields/ColorCustomField';
import SignatureCustomField from '../../atoms/task-panel-customfields/SignatureCustomField';

const FIELD_ICONS = {
  'status': CircleDot, 'link': Link2, 'date': Calendar, 'text': Type,
  'dropdown': ChevronDown, 'priority': Flag, 'number': Hash, 'cost': DollarSign,
  'multi-select': LayoutList, 'email': Mail, 'rating': Star, 'phone': Phone,
  'long text': AlignLeft, 'rich text': FileText, 'checkbox': CheckSquare,
  'people': Users, 'formula': Calculator, 'full-name': UserRound, 'address': MapPin,
  'datetime': Clock, 'time': AlarmClock, 'currency': Banknote, 'percentage': Percent, 'duration': Timer,
  'id-reference': Binary, 'country': Globe, 'color': Palette,
  'file-upload': Paperclip, 'camera': Camera, 'signature': PenLine, 'assignee': User,
};

const FIELD_BADGE_STYLES = {
  'status':       { bg: 'rgba(124,58,216,0.1)',  border: 'rgba(124,58,216,0.18)',  color: '#7c3aed' },
  'link':         { bg: 'rgba(249,115,22,0.1)',  border: 'rgba(249,115,22,0.18)',  color: '#f97316' },
  'date':         { bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.18)',   color: '#ef4444' },
  'text':         { bg: 'rgba(20,184,166,0.1)',  border: 'rgba(20,184,166,0.18)',  color: '#14b8a6' },
  'dropdown':     { bg: 'rgba(236,72,153,0.1)',  border: 'rgba(236,72,153,0.18)',  color: '#ec4899' },
  'priority':     { bg: 'rgba(249,115,22,0.1)',  border: 'rgba(249,115,22,0.18)',  color: '#f97316' },
  'number':       { bg: 'rgba(244,63,94,0.1)',   border: 'rgba(244,63,94,0.18)',   color: '#f43f5e' },
  'cost':         { bg: 'rgba(16,185,129,0.1)',  border: 'rgba(16,185,129,0.18)',  color: '#10b981' },
  'multi-select': { bg: 'rgba(217,70,239,0.1)',  border: 'rgba(217,70,239,0.18)',  color: '#d946ef' },
  'email':        { bg: 'rgba(59,130,246,0.1)',  border: 'rgba(59,130,246,0.18)',  color: '#3b82f6' },
  'rating':       { bg: 'rgba(234,179,8,0.1)',   border: 'rgba(234,179,8,0.18)',   color: '#ca8a04' },
  'phone':        { bg: 'rgba(14,165,233,0.1)',  border: 'rgba(14,165,233,0.18)',  color: '#0ea5e9' },
  'long text':    { bg: 'rgba(34,197,94,0.1)',   border: 'rgba(34,197,94,0.18)',   color: '#16a34a' },
  'rich text':    { bg: 'rgba(20,184,166,0.1)',  border: 'rgba(20,184,166,0.18)',  color: '#0d9488' },
  'checkbox':     { bg: 'rgba(59,130,246,0.1)',  border: 'rgba(59,130,246,0.18)',  color: '#2563eb' },
  'people':       { bg: 'rgba(6,182,212,0.1)',   border: 'rgba(6,182,212,0.18)',   color: '#0891b2' },
  'formula':      { bg: 'rgba(99,102,241,0.1)',  border: 'rgba(99,102,241,0.18)',  color: '#4f46e5' },
  'full-name':    { bg: 'rgba(6,182,212,0.1)',   border: 'rgba(6,182,212,0.18)',   color: '#06b6d4' },
  'address':      { bg: 'rgba(251,146,60,0.1)',  border: 'rgba(251,146,60,0.18)',  color: '#ea580c' },
  'datetime':     { bg: 'rgba(168,85,247,0.1)',  border: 'rgba(168,85,247,0.18)',  color: '#9333ea' },
  'time':         { bg: 'rgba(79,70,229,0.1)',   border: 'rgba(79,70,229,0.18)',   color: '#4f46e5' },
  'currency':     { bg: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.18)',  color: '#d97706' },
  'percentage':   { bg: 'rgba(168,85,247,0.1)',  border: 'rgba(168,85,247,0.18)',  color: '#7c3aed' },
  'duration':     { bg: 'rgba(6,182,212,0.1)',   border: 'rgba(6,182,212,0.18)',   color: '#0891b2' },
  'id-reference': { bg: 'rgba(100,116,139,0.1)', border: 'rgba(100,116,139,0.18)', color: '#475569' },
  'country':      { bg: 'rgba(20,184,166,0.1)',  border: 'rgba(20,184,166,0.18)',  color: '#0f766e' },
  'color':        { bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.18)',   color: '#dc2626' },
  'file-upload':  { bg: 'rgba(20,184,166,0.1)',  border: 'rgba(20,184,166,0.18)',  color: '#0d9488' },
  'camera':       { bg: 'rgba(168,85,247,0.1)',  border: 'rgba(168,85,247,0.18)',  color: '#9333ea' },
  'signature':    { bg: 'rgba(20,184,166,0.1)',  border: 'rgba(20,184,166,0.18)',  color: '#14b8a6' },
  'assignee':     { bg: 'rgba(14,165,233,0.1)',  border: 'rgba(14,165,233,0.18)',  color: '#0ea5e9' },
};
const DEFAULT_FIELD_BADGE = { bg: 'rgba(99,102,241,0.1)', border: 'rgba(99,102,241,0.18)', color: '#4f46e5' };

// ── inline text / number editor ──────────────────────────────────────────────
const InlineEditor = ({ value, type = 'text', placeholder = 'Empty', prefix, suffix, onSave }) => {
  const hasValue = value !== null && value !== undefined && value !== '';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(hasValue ? String(value) : '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value !== null && value !== undefined && value !== '' ? String(value) : ''); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed !== String(value ?? '')) onSave(trimmed || null);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 px-3 py-1.5 w-full" style={{ background: 'rgba(99,102,241,0.04)', borderRadius: '0 8px 8px 0' }}>
        {prefix && <span className="text-[13px] flex-shrink-0" style={{ color: 'rgba(15,15,25,0.45)' }}>{prefix}</span>}
        <input
          ref={inputRef}
          type={type}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(hasValue ? String(value) : ''); setEditing(false); } }}
          className="flex-1 bg-transparent outline-none text-[13px] min-w-0"
          style={{ color: 'rgba(15,15,25,0.8)' }}
        />
        {suffix && <span className="text-[13px] flex-shrink-0" style={{ color: 'rgba(15,15,25,0.45)' }}>{suffix}</span>}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="flex items-center gap-1.5 px-3 py-2 w-full text-left hover:bg-indigo-50/50 rounded-r transition-colors group"
    >
      {prefix && hasValue && <span className="text-[13px] flex-shrink-0" style={{ color: 'rgba(15,15,25,0.5)' }}>{prefix}</span>}
      <span className="text-[13px] truncate" style={{ color: hasValue ? 'rgba(15,15,25,0.8)' : 'rgba(15,15,25,0.28)' }}>
        {hasValue ? String(value) : placeholder}
      </span>
      {suffix && hasValue && <span className="text-[13px] flex-shrink-0" style={{ color: 'rgba(15,15,25,0.5)' }}>{suffix}</span>}
      <svg className="ml-auto flex-shrink-0 opacity-0 group-hover:opacity-40 transition-opacity" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
      </svg>
    </button>
  );
};

// ── checkbox toggle ───────────────────────────────────────────────────────────
const CheckboxField = ({ value, onChange }) => {
  const checked = value === true || value === 'true';
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex items-center gap-2.5 px-3 py-2 w-full text-left hover:bg-indigo-50/50 rounded-r transition-colors"
    >
      <div
        className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-all"
        style={{
          border: `2px solid ${checked ? '#6366f1' : 'rgba(15,15,25,0.22)'}`,
          background: checked ? '#6366f1' : 'transparent',
        }}
      >
        {checked && (
          <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
            <path d="M1.5 5L3.8 7.5L8.5 2.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
      <span className="text-[13px] font-medium" style={{ color: checked ? '#6366f1' : 'rgba(15,15,25,0.4)' }}>
        {checked ? 'Yes' : 'No'}
      </span>
    </button>
  );
};

// ── file-upload field ─────────────────────────────────────────────────────────
const FileUploadFieldWidget = ({ value, onSave }) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  // value is { fileId, fileName } or a legacy plain string (filename only)
  const fileId = value?.fileId;
  const fileName = value?.fileName || (typeof value === 'string' ? value : null);

  useEffect(() => {
    if (!fileId) { setDownloadUrl(null); return; }
    dispatch(getFileActionWithRetry(fileId)).then((res) => {
      if (res?.signedUrl) setDownloadUrl(res.signedUrl);
    });
  }, [fileId, dispatch]);

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fileIds = await uploadFilesHelper([file], dispatch);
      const newFileId = fileIds?.[0];
      if (newFileId) onSave({ fileId: newFileId, fileName: file.name });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="px-3 py-1.5 w-full">
      {fileName ? (
        <div className="flex items-center gap-1.5 group">
          {downloadUrl ? (
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 flex-1 min-w-0 hover:underline text-[12.5px] font-medium"
              style={{ color: '#4f46e5' }}
            >
              <Paperclip size={12} className="flex-shrink-0" />
              <span className="truncate">{fileName}</span>
            </a>
          ) : (
            <span className="flex items-center gap-1.5 flex-1 min-w-0 text-[12.5px]" style={{ color: 'rgba(15,15,25,0.65)' }}>
              <Paperclip size={12} className="flex-shrink-0" />
              <span className="truncate">{fileName}</span>
            </span>
          )}
          <button
            type="button"
            onClick={() => { onSave(null); setDownloadUrl(null); }}
            className="flex-shrink-0 opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity text-slate-400 hover:text-red-500"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => !uploading && inputRef.current?.click()}
          className="flex items-center gap-1.5 text-[12.5px] hover:text-indigo-500 transition-colors"
          style={{ color: 'rgba(15,15,25,0.35)' }}
        >
          <Paperclip size={13} />
          <span>{uploading ? 'Uploading…' : 'Attach file'}</span>
        </button>
      )}
      <input ref={inputRef} type="file" className="hidden" onChange={handleChange} />
    </div>
  );
};

// ── camera field ─────────────────────────────────────────────────────────────
const CameraFieldWidget = ({ value, onSave }) => {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onSave(ev.target.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="px-3 py-1.5 w-full">
      {value ? (
        <div className="relative inline-block">
          <img
            src={value}
            alt="Uploaded"
            className="max-h-20 rounded-lg object-cover border border-slate-200 cursor-pointer"
            onClick={() => inputRef.current?.click()}
          />
          <button
            type="button"
            onClick={() => onSave(null)}
            className="absolute -top-1.5 -right-1.5 bg-white border border-slate-200 text-slate-500 hover:text-red-500 rounded-full w-5 h-5 flex items-center justify-center text-[10px] shadow transition-colors"
          >✕</button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex items-center gap-1.5 text-[12.5px] hover:text-indigo-500 transition-colors"
          style={{ color: 'rgba(15,15,25,0.35)' }}
        >
          <Camera size={13} />
          <span>Upload photo</span>
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleChange} />
    </div>
  );
};

// ── link field ────────────────────────────────────────────────────────────────
const LinkField = ({ value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = () => { setEditing(false); if (draft !== (value || '')) onSave(draft || null); };
  const valid = isValidURL(value);

  if (editing) {
    return (
      <div className="flex items-center gap-1.5 px-3 py-1.5 w-full">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(99,102,241,0.5)" strokeWidth="2" strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
        <input
          ref={inputRef}
          type="text"
          value={draft}
          placeholder="https://..."
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); } }}
          className="flex-1 bg-transparent outline-none text-[13px] min-w-0"
          style={{ color: '#4f46e5' }}
        />
      </div>
    );
  }

  if (valid) {
    return (
      <div className="flex items-center gap-1.5 px-3 py-1.5 w-full group">
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          className="flex items-center gap-1.5 flex-1 min-w-0 hover:underline"
          style={{ color: '#4f46e5' }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="flex-shrink-0"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
          <span className="text-[12.5px] truncate font-medium">
            {(() => { try { return new URL(value).hostname; } catch { return value; } })()}
          </span>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="flex-shrink-0 opacity-60"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
        </a>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="flex-shrink-0 opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity p-1 rounded hover:bg-indigo-50"
          title="Edit URL"
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="flex items-center gap-1.5 px-3 py-2 w-full text-left hover:bg-indigo-50/50 rounded-r transition-colors group"
    >
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(15,15,25,0.25)" strokeWidth="2" strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
      <span className="text-[13px]" style={{ color: 'rgba(15,15,25,0.28)' }}>Add link…</span>
    </button>
  );
};

// ── main component ────────────────────────────────────────────────────────────
const TaskCustomFields = (props) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [customFields, setCustomFields] = useState(props?.customFields || []);
  const [open, setOpen] = useState(false);
  const [searchOptions, setSearchOptions] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState('');
  const [selectedAssignees, setSelectedAssignees] = useState({});

  const hide = () => setOpen(false);

  // Close dropdowns when sidebar scrolls
  const scrollInitials = useRef(new WeakMap());
  useEffect(() => {
    if (!dropdownVisible) return;
    scrollInitials.current = new WeakMap();
    const handleScroll = (e) => {
      const target = e.target;
      if (!scrollInitials.current.has(target)) {
        scrollInitials.current.set(target, { top: target.scrollTop, left: target.scrollLeft });
        return;
      }
      const initial = scrollInitials.current.get(target);
      if (Math.abs(target.scrollTop - initial.top) > 40 || Math.abs(target.scrollLeft - initial.left) > 40) {
        setDropdownVisible(null);
      }
    };
    document.addEventListener('scroll', handleScroll, { capture: true });
    return () => document.removeEventListener('scroll', handleScroll, { capture: true });
  }, [dropdownVisible]);

  const debouncedFilterTasks = useCallback(
    _debounce((id, value, taskId) => handleChangeCustomField(id, value, taskId), 1000),
    []
  );

  useEffect(() => {
    props?.customFields && setCustomFields(
      props.customFields.slice().sort((a, b) => (a?.name > b?.name ? 1 : -1))
    );
  }, [props?.customFields]);

  const handleChangeCustomField = (customFieldId, val, taskId, e) => {
    if (e) e.preventDefault();
    if (loading) return;
    setLoading(true);
    dispatch(editTaskCustomFieldAction({ _id: taskId, customFieldId, customFieldValue: val }, () => setLoading(false)));
  };

  const updateField = (id, value) => {
    setCustomFields(prev => prev.map(f => f?._id == id ? { ...f, value } : f));
    debouncedFilterTasks(id, value, props?.id);
  };

  const generateFieldValue = (field) => {
    switch (field?.type) {
      // ── simple text / number types ──────────────────────────────────────
      case customFieldTypes.text:
        return (
          <InlineEditor
            value={field?.value}
            type="text"
            placeholder="Empty"
            onSave={v => updateField(field._id, v)}
          />
        );

      case customFieldTypes.number:
        return (
          <InlineEditor
            value={field?.value}
            type="number"
            placeholder="0"
            onSave={v => updateField(field._id, v)}
          />
        );

      case customFieldTypes.cost:
        return (
          <InlineEditor
            value={field?.value}
            type="number"
            placeholder="0.00"
            prefix="$"
            onSave={v => updateField(field._id, v)}
          />
        );

      case customFieldTypes.currency:
        return (
          <InlineEditor
            value={field?.value}
            type="number"
            placeholder="0.00"
            prefix="$"
            onSave={v => updateField(field._id, v)}
          />
        );

      case customFieldTypes.address:
        return (
          <InlineEditor
            value={field?.value}
            type="text"
            placeholder="Enter address…"
            onSave={v => updateField(field._id, v)}
          />
        );

      case customFieldTypes.idReference:
        return (
          <InlineEditor
            value={field?.value}
            type="text"
            placeholder="REF-001"
            prefix="#"
            onSave={v => updateField(field._id, v)}
          />
        );

      // ── link ────────────────────────────────────────────────────────────
      case customFieldTypes.link:
        return (
          <LinkField
            value={field?.value}
            onSave={v => updateField(field._id, v)}
          />
        );

      // ── checkbox ────────────────────────────────────────────────────────
      case customFieldTypes.checkbox:
        return (
          <MultiSelectCustomField field={field} updateField={updateField} />
        );

      // ── dropdown / status ───────────────────────────────────────────────
      case customFieldTypes.dropdown:
      case customFieldTypes.status: {
        const selectedVal = field?.possibleValues?.find(v => v?._id == field?.value);
        const filteredItems = field?.possibleValues?.filter(val =>
          val?.label?.toLowerCase().includes(searchOptions.toLowerCase())
        ) || [];
        return (
          <Dropdown
            trigger={['click']}
            open={dropdownVisible === field?._id}
            onOpenChange={visible => {
              setDropdownVisible(visible ? field?._id : null);
              if (!visible) setSearchOptions('');
            }}
            dropdownRender={() => (
              <div className="bg-white" style={{ minWidth: 220, maxHeight: 320, overflow: 'hidden', display: 'flex', flexDirection: 'column', borderRadius: 12, border: '1px solid rgba(99,102,241,0.14)', boxShadow: '0 8px 32px rgba(99,102,241,0.12)' }}>
                <div className="px-3 pt-3 pb-2 flex-shrink-0" style={{ borderBottom: '1px solid rgba(99,102,241,0.08)' }}>
                  <div className="flex items-center gap-2 px-2.5 rounded-lg" style={{ background: 'rgba(99,102,241,0.05)', border: '1px solid rgba(99,102,241,0.15)', height: 32 }}>
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="rgba(99,102,241,0.5)" strokeWidth="1.8" strokeLinecap="round"><circle cx="6.5" cy="6.5" r="4.5"/><line x1="10.5" y1="10.5" x2="14" y2="14"/></svg>
                    <input autoFocus className="flex-1 bg-transparent outline-none text-[12.5px]" style={{ color: 'rgba(15,15,25,0.8)' }} placeholder="Search…" value={searchOptions} onChange={e => setSearchOptions(e.target.value)} />
                  </div>
                </div>
                <div className="overflow-y-auto py-1.5 px-1.5" style={{ maxHeight: 240 }}>
                  <div className="flex items-center px-2.5 py-1.5 rounded-lg cursor-pointer text-[12px] mb-0.5" style={{ color: 'rgba(15,15,25,0.35)' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.05)'} onMouseLeave={e => e.currentTarget.style.background = ''} onClick={() => { updateField(field._id, null); setDropdownVisible(null); }}>
                    Clear
                  </div>
                  {filteredItems.map(val => {
                    const isSelected = selectedVal?._id === val?._id;
                    return (
                      <div key={val?._id?.toString()} className="flex items-center justify-between px-2.5 py-1.5 rounded-lg cursor-pointer" style={{ background: isSelected ? 'rgba(99,102,241,0.06)' : '' }} onMouseEnter={e => e.currentTarget.style.background = isSelected ? 'rgba(99,102,241,0.09)' : 'rgba(99,102,241,0.04)'} onMouseLeave={e => e.currentTarget.style.background = isSelected ? 'rgba(99,102,241,0.06)' : ''} onClick={() => { updateField(field._id, val?._id?.toString()); setDropdownVisible(null); }}>
                        <Tag color={getTagColor(val?.metadata?.color)} className="text-xs text-white m-0">
                          <FormattedMessage id={`${val?.label}`.replaceAll(' ', '_').toUpperCase()} defaultMessage={val?.label} />
                        </Tag>
                        {isSelected && <svg width="12" height="12" viewBox="0 0 16 16" fill="#4f46e5"><path d="m2.457 8.516.969-.99 2.516 2.481 5.324-5.304.985.989-6.309 6.284z"/></svg>}
                      </div>
                    );
                  })}
                  {filteredItems.length === 0 && <div className="px-3 py-3 text-center text-[12px]" style={{ color: 'rgba(15,15,25,0.35)' }}>No results</div>}
                </div>
              </div>
            )}
          >
            <button type="button" className="flex items-center justify-between px-3 py-2 cursor-pointer w-full hover:bg-indigo-50/50 rounded-r transition-colors" style={{ minHeight: 40 }}>
              {selectedVal?.label
                ? <Tag color={getTagColor(selectedVal?.metadata?.color)} className="text-xs text-white cursor-pointer m-0"><FormattedMessage id={`${selectedVal?.label}`.replaceAll(' ', '_').toUpperCase()} defaultMessage={selectedVal?.label} /></Tag>
                : <span className="text-[13px]" style={{ color: 'rgba(15,15,25,0.28)' }}>Select…</span>
              }
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="rgba(15,15,25,0.3)" strokeWidth="2" strokeLinecap="round"><polyline points="4 6 8 10 12 6"/></svg>
            </button>
          </Dropdown>
        );
      }

      // ── multi-select ────────────────────────────────────────────────────
      case customFieldTypes.multiSelect:
        return (
          <MultiSelectCustomField field={field} updateField={updateField} />
        );

      // ── assignee ────────────────────────────────────────────────────────
      case customFieldTypes.assignee: {
        const fieldId = field?._id;
        const currentAssignees = Array.isArray(field?.value)
          ? field.value.map(a => ({ value: a?.assigneeId, label: a?.label, ...a }))
          : [];
        const selectedForThisField = selectedAssignees[fieldId] || currentAssignees;
        return (
          <div className="w-full px-3 py-1">
            <ConnectedAssigneeSelect
              iconSize={26}
              count={2}
              currentValue={selectedForThisField}
              setCurrentValue={newValue => {
                setSelectedAssignees(prev => ({ ...prev, [fieldId]: newValue }));
                const assigneeData = newValue?.map(a => ({ assigneeId: a?.value, label: a?.label, email: a?.email, avi: a?.avi, ...a }));
                updateField(fieldId, assigneeData);
              }}
              isAddAssignee={true}
            />
          </div>
        );
      }

      // ── priority ────────────────────────────────────────────────────────
      case customFieldTypes.priority:
        return (
          <PriorityDropdown
            task={{ priority: field?.value || 'nill' }}
            onClick={p => updateField(field._id, p?.rank)}
            isTask
            small
          />
        );

      // ── date ────────────────────────────────────────────────────────────
      case customFieldTypes.date:
        return (
          <div className="flex items-center px-3 w-full hover:bg-indigo-50/50 rounded-r transition-colors">
            <CalendarDropdown
              task={{ dueDate: field?.value }}
              onClick={p => updateField(field._id, p)}
              isTask
            />
          </div>
        );

      // ── email ────────────────────────────────────────────────────────────
      case customFieldTypes.email:
        return (
          <EmailCustomField
            field={field}
            editingFieldId={editingFieldId}
            setEditingFieldId={setEditingFieldId}
            updateField={updateField}
          />
        );

      // ── rating ───────────────────────────────────────────────────────────
      case customFieldTypes.rating:
        return (
          <RatingCustomField
            field={field}
            value={field?.value}
            onChange={updateField}
          />
        );

      // ── phone ────────────────────────────────────────────────────────────
      case customFieldTypes.phone:
        return (
          <PhoneCustomField
            field={field}
            editingFieldId={editingFieldId}
            setEditingFieldId={setEditingFieldId}
            updateField={updateField}
          />
        );

      // ── long text ─────────────────────────────────────────────────────────
      case customFieldTypes.longText:
        return (
          <LongTextCustomField
            field={field}
            onChange={val => updateField(field._id, val)}
          />
        );

      // ── rich text ─────────────────────────────────────────────────────────
      case customFieldTypes.richText:
        return (
          <RichTextCustomField
            field={field}
            onChange={val => updateField(field._id, val)}
          />
        );

      // ── full name ─────────────────────────────────────────────────────────
      case customFieldTypes.fullName:
        return (
          <InlineEditor
            value={field?.value}
            type="text"
            placeholder="Enter full name…"
            onSave={v => updateField(field._id, v)}
          />
        );

      // ── color ─────────────────────────────────────────────────────────────
      case customFieldTypes.color:
        return (
          <ColorCustomField
            field={field}
            updateField={updateField}
          />
        );

      // ── signature ─────────────────────────────────────────────────────────
      case customFieldTypes.signature:
        return (
          <SignatureCustomField
            field={field}
            updateField={updateField}
          />
        );

      // ── datetime ─────────────────────────────────────────────────────────
      case customFieldTypes.datetime:
        return (
          <InlineEditor
            value={field?.value}
            type="datetime-local"
            placeholder="Select date & time…"
            onSave={v => updateField(field._id, v)}
          />
        );

      // ── time ─────────────────────────────────────────────────────────────
      case customFieldTypes.time:
        return (
          <div className="px-3 py-1 w-full">
            <TimePicker
              value={field?.value || ''}
              onChange={(v) => updateField(field._id, v || null)}
              compact
            />
          </div>
        );

      // ── percentage ───────────────────────────────────────────────────────
      case customFieldTypes.percentage:
        return (
          <InlineEditor
            value={field?.value}
            type="number"
            placeholder="0"
            suffix="%"
            onSave={v => updateField(field._id, v)}
          />
        );

      // ── country ──────────────────────────────────────────────────────────
      case customFieldTypes.country:
        return (
          <InlineEditor
            value={field?.value}
            type="text"
            placeholder="Enter country…"
            onSave={v => updateField(field._id, v)}
          />
        );

      // ── file-upload ──────────────────────────────────────────────────────
      case customFieldTypes.fileUpload:
        return (
          <FileUploadFieldWidget
            value={field?.value}
            onSave={v => updateField(field._id, v)}
          />
        );

      // ── camera ───────────────────────────────────────────────────────────
      case customFieldTypes.camera:
        return (
          <CameraFieldWidget
            value={field?.value}
            onSave={v => updateField(field._id, v)}
          />
        );

      default:
        return (
          <span className="px-3 py-2 text-[13px]" style={{ color: 'rgba(15,15,25,0.28)' }}>—</span>
        );
    }
  };

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(99,102,241,0.12)' }}>
      {customFields?.map((field, index) => {
        const projects = props?.projects?.filter(p => props?.projectCount?.[p?._id] != undefined);
        let project = projects?.[0] || {};
        projects?.forEach(proj => {
          proj?.customFields?.forEach(pCus => { if (pCus?._id == field?._id) project = proj; });
        });

        const FieldIcon = FIELD_ICONS[field?.type] || Hash;
        const fieldStyle = FIELD_BADGE_STYLES[field?.type] || DEFAULT_FIELD_BADGE;
        const isLast = index === customFields?.length - 1;

        return (
          <div
            key={field?._id || index}
            className="flex items-stretch"
            style={{ borderBottom: isLast ? 'none' : '1px solid rgba(99,102,241,0.08)', minHeight: 40 }}
          >
            {/* Label column */}
            <Tooltip title="Edit field settings" mouseEnterDelay={0.6} placement="topLeft">
              <button
                type="button"
                className="flex items-center gap-2 cursor-pointer group flex-shrink-0"
                style={{ width: '42%', borderRight: '1px solid rgba(99,102,241,0.08)', padding: '8px 10px' }}
                onClick={() => dispatch(toggleEditCustomFieldModal(true, field, project))}
              >
                <div className="size-6 flex-shrink-0 rounded-md flex justify-center items-center" style={{ background: fieldStyle.bg, border: `1px solid ${fieldStyle.border}` }}>
                  <FieldIcon size={12} strokeWidth={1.5} color={fieldStyle.color} />
                </div>
                <span className="truncate text-[12.5px] font-medium group-hover:underline" style={{ color: 'rgba(15,15,25,0.65)' }}>
                  <FormattedMessage id={`${field?.name}`.replaceAll(' ', '_').toUpperCase()} defaultMessage={field?.name} />
                </span>
              </button>
            </Tooltip>

            {/* Value column */}
            <div className="flex items-center" style={{ flex: 1, minWidth: 0 }}>
              {generateFieldValue(field)}
            </div>
          </div>
        );
      })}

      {customFields?.length === 0 && (
        <div className="px-4 py-3 text-[12px]" style={{ color: 'rgba(15,15,25,0.35)' }}>
          <FormattedMessage id="NO_CUSTOM_FIELDS" defaultMessage="No Custom Fields" />
        </div>
      )}
    </div>
  );
};

export default TaskCustomFields;
